import React, { useState, useEffect } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import LegalDocumentModal from '../Legal/LegalDocumentModal';

function SupplierLogin() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [redirectMessage, setRedirectMessage] = useState('');
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [userId, setUserId] = useState(null);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [_resendSuccess, setResendSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, fileName: null, title: '' });
  const [legalDocs, setLegalDocs] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, _login, setUser, reinitializeAuth: _reinitializeAuth } = useAuth();

  useEffect(() => {
    const loadLegalDocs = async () => {
      try {
        const response = await api.get('/api/legal', { params: { userType: 'supplier' } });
        setLegalDocs(response.data?.documents || []);
      } catch {
        setLegalDocs([]);
      }
    };

    loadLegalDocs();
  }, []);

  const getLegalDoc = (...fileNames) => legalDocs.find((doc) => fileNames.includes(doc.fileName));
  const legalLinks = [
    getLegalDoc('business-terms'),
    getLegalDoc('privacy-policy'),
    getLegalDoc('cookie-policy', 'cookies-policy'),
  ].filter(Boolean);
  const getLegalPath = (fileName) => {
    if (fileName === 'business-terms') return '/business-terms';
    if (fileName === 'privacy-policy') return '/privacy-policy';
    if (fileName === 'cookie-policy' || fileName === 'cookies-policy') return '/cookie-policy';
    return '/privacy-policy';
  };

  // Redirect to dashboard when already logged in as supplier
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    if (!authLoading && token && userType === 'supplier' && user?.role === 'supplier') {
      navigate('/supplier/dashboard', { replace: true });
      return;
    }
  }, [authLoading, user, navigate]);

  // Clear tokens only when not logged in (allows fresh login for different account)
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    const isLoggedInSupplier = token && userType === 'supplier';
    if (!location.state?.from && !isLoggedInSupplier) {
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      delete api.defaults.headers.common['x-auth-token'];
    }

    // Check if we have a redirected state with email and message
    if (location.state?.email) {
      setFormData(prev => ({
        ...prev,
        email: location.state.email
      }));
      
      if (location.state.message) {
        setRedirectMessage(location.state.message);
      }
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) return;
    
    setLoading(true);
    setError('');

    try {
      // Trim inputs to prevent whitespace issues
      const email = formData.email.trim();
      const password = formData.password;

      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      // Get deviceId from localStorage if available
      const deviceId = localStorage.getItem('deviceId');

      // Call supplier login endpoint directly
      const response = await api.post('/api/supplier/login', { email, password, deviceId });

      // Check if verification is required
      if (response.data.requiresVerification) {
        setUserId(response.data.userId);
        setVerificationStep(true);
        
        // Handle email sending status
        if (response.data.emailSent === false) {
          setError('We could not send the verification email. Please use the resend option below.');
        } else {
          setError(''); // Clear error - don't show the initial message
        }
        return;
      }

      // Store authentication data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userId', response.data.user.id);
      localStorage.setItem('userType', 'supplier');

      // Set token in API headers
      api.defaults.headers.common['x-auth-token'] = response.data.token;

      setUser(response.data.user);

      navigate('/supplier/dashboard');
    } catch (error) {
      // Check if platform is closed/maintenance
      if (error.response?.status === 503 && error.response?.data?.maintenance) {
        // Platform is closed - PlatformStatusCheck should handle this, but just in case
        setError({
          message: error.response.data.message,
          maintenance: true
        });
        return;
      }

      // Check if this is a pre-registered user error
      if (error.response?.data?.isPreRegistered) {
        setError({
          message: error.response.data.message,
          isPreRegistered: true
        });
      } else {
        setError(
          error.response?.data?.message || 
          error.message || 
          'An error occurred during login. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate code format before sending to server
      if (!verificationCode || verificationCode.trim().length !== 6) {
        setError('Please enter a valid 6-digit verification code');
        setLoading(false);
        return;
      }

      const response = await api.post('/api/supplier/verify-login', {
        userId,
        code: verificationCode.trim(),
        rememberDevice,
        deviceName: navigator.userAgent // Use user agent as device name
      });

      // Store authentication data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userId', response.data.user.id);
      localStorage.setItem('userType', 'supplier');

      // Store device ID if remember device was enabled
      if (response.data.deviceId) {
        localStorage.setItem('deviceId', response.data.deviceId);
      }

      // Set token in API headers
      api.defaults.headers.common['x-auth-token'] = response.data.token;

      // Set user data immediately from response to prevent loading timeout
      if (response.data.user) {
        const userData = {
          ...response.data.user,
          role: 'supplier',
          id: response.data.user.id || response.data.user._id,
          _id: response.data.user.id || response.data.user._id,
          isEmailVerified: true // Ensure email is marked as verified
        };
        setUser(userData);
      }

      // Clear verification state
      setVerificationStep(false);
      setVerificationCode('');

      // Use a small delay to ensure state updates propagate
      setTimeout(() => {
        navigate('/supplier/dashboard', { replace: true });
      }, 50);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Error verifying code';
      setError(errorMessage);

      // If the code is expired, suggest resending
      if (errorMessage.includes('expired')) {
        setError(errorMessage + ' Click "Resend Code" to get a new verification code.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.post('/api/supplier/resend-verification', {
        userId
      });

      if (response.data.emailSent !== false) {
        setResendSuccess(true);
        setError('A new verification code has been sent to your email');
      } else {
        setError('Failed to send verification email. Please try again or contact support.');
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        setResendSuccess(false);
      }, 5000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
        error.message || 
        'Failed to resend verification code';

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundImage: `url(${process.env.PUBLIC_URL}/img/brand-bg.jpg)`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      {/* Logo */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 4
        }}
      >
        <img src={`${process.env.PUBLIC_URL}/img/idotoosuppliers.svg`} alt="Idotoo Suppliers Logo" style={{ width: '100px', height: '50px' }} />
      </Box>

      {/* Login Card */}
      <Paper 
        elevation={0}
        sx={{ 
          backgroundColor: 'background.paper',
          maxWidth: '400px',
          width: '100%',
          mx: 2,
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 500,
              color: 'text.primary',
              mb: 3
            }}
          >
            Supplier Login
          </Typography>
              
          {redirectMessage && (
            <Alert severity="info" sx={{ mb: 3 }}>
              {redirectMessage}
            </Alert>
          )}
          {error && (
            <Alert 
              severity={typeof error === 'object' && error.isPreRegistered ? 'info' : 'error'}
              icon={false}
              sx={{ 
                mb: 3,
                ...(typeof error === 'object' && error.isPreRegistered ? {
                  backgroundColor: '#fff3f8',
                  border: '1px solid #ffb3d9',
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    width: '100%',
                    textAlign: 'center'
                  }
                } : {})
              }}
            >
              <Typography variant="body2" sx={{ 
                textAlign: typeof error === 'object' && error.isPreRegistered ? 'center' : 'left'
              }}>
                {typeof error === 'object' && error.isPreRegistered ? (
                  <>💕 {error.message} 💕</>
                ) : (
                  typeof error === 'object' ? error.message : error
                )}
              </Typography>
              {typeof error === 'object' && error.isPreRegistered && (
                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Button
                    variant="outlined"
                    href="https://idotoo.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ 
                      textTransform: 'none',
                      borderColor: '#e91e63',
                      color: '#e91e63',
                      '&:hover': {
                        borderColor: '#c2185b',
                        backgroundColor: '#fff3f8'
                      }
                    }}
                  >
                    Visit idotoo.ai
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              placeholder="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              autoFocus
              sx={{ 
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff'
                }
              }}
            />
            <TextField
              fullWidth
              placeholder="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              sx={{ 
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ 
                py: 1.5,
                mb: 2,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                border: '1px solid #000',
                color: 'rgba(255, 255, 255, 1)',
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign in'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link 
                component={RouterLink} 
                to="/forgot-password" 
                underline="hover" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.875rem'
                }}
              >
                Forgotten password?
              </Link>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Sign Up Card */}
      <Paper 
        elevation={0}
        sx={{ 
          backgroundColor: 'secondary.dark',
          maxWidth: '400px',
          width: '100%',
          mx: 2,
          mt: 2,
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography 
            variant="h6" 
            component="h3"
            sx={{ 
              fontWeight: 600,
              color: 'text.primary',
              mb: 1
            }}
          >
            No supplier account?
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#000000',
              mb: 3
            }}
          >
            Join our network of wedding suppliers and grow your business!
          </Typography>
          <Button
            onClick={(e) => {
              e.preventDefault();
              navigate('/supplier/register');
            }}
            variant="contained"
            fullWidth
            sx={{ 
              py: 1.5,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              backgroundColor: 'background.paper',
              color: 'text.primary',
              '&:hover': {
                backgroundColor: 'grey.100'
              }
            }}
          >
            Become a supplier
          </Button>
        </Box>
      </Paper>

      {/* Regular User Login Link */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Are you planning a wedding?{' '}
          <Link 
            component="button"
            onClick={(e) => {
              e.preventDefault();
              navigate('/login');
            }}
            sx={{ 
              color: 'primary.main',
              fontWeight: 500,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            Login here
          </Link>
        </Typography>
      </Box>

      {legalLinks.length > 0 && (
        <Typography
          variant="caption"
          sx={{ mt: 2, color: 'text.secondary', textAlign: 'center', px: 2 }}
        >
          By continuing, you agree to our{' '}
          {legalLinks.map((doc, index) => (
            <React.Fragment key={doc.fileName}>
              <Link
                component={RouterLink}
                to={getLegalPath(doc.fileName)}
                sx={{ fontSize: 'inherit', color: 'text.secondary', textDecoration: 'underline' }}
              >
                {doc.title}
              </Link>
              {index < legalLinks.length - 2 ? ', ' : index === legalLinks.length - 2 ? ' and ' : '.'}
            </React.Fragment>
          ))}
        </Typography>
      )}

      {/* Verification Dialog */}
      <Dialog 
        open={verificationStep} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, #e7548b 0%, #f4a8c4 100%)`,
            px: 3,
            py: 2.5,
            textAlign: 'center'
          }}
        >
          <DialogTitle 
            sx={{ 
              color: 'white', 
              p: 0, 
              pb: 0, 
              borderBottom: 'none',
              fontWeight: 500,
              fontSize: '1.5rem'
            }}
          >
            Enter Verification Code
          </DialogTitle>
        </Box>
        <DialogContent sx={{ px: 3, pt: 3, pb: 2 }}>
          <TextField
            fullWidth
            label="Verification Code"
            value={verificationCode}
            onChange={(e) => {
              // Only allow digits and limit to 6 characters
              const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
              setVerificationCode(value);
            }}
            variant="outlined"
            placeholder="000000"
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { 
                textAlign: 'center',
                fontSize: '24px',
                letterSpacing: '8px',
                fontWeight: 500,
                padding: '14px 0',
                height: 'auto',
                lineHeight: 'normal'
              }
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                alignItems: 'center',
                '& input': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                },
                '& fieldset': {
                  borderWidth: 2
                }
              },
              '& .MuiInputBase-input': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 'auto',
                py: 1.5
              }
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberDevice}
                onChange={(e) => setRememberDevice(e.target.checked)}
                color="primary"
              />
            }
            label="Remember this device for 30 days"
            sx={{ mb: 2 }}
          />
          {error && (
            <Alert severity={error.includes('sent to your email') ? 'success' : 'error'} sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          <Button
            variant="text"
            color="primary"
            onClick={handleResendCode}
            disabled={loading}
            sx={{ 
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            Resend Code
          </Button>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleVerificationSubmit}
            disabled={loading || verificationCode.length !== 6}
            fullWidth
            size="large"
            sx={{
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              bgcolor: '#e7548b',
              '&:hover': {
                bgcolor: '#d43d77'
              }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
          </Button>
        </DialogActions>
      </Dialog>

      <LegalDocumentModal
        open={legalModal.open}
        onClose={() => setLegalModal({ open: false, fileName: null, title: '' })}
        fileName={legalModal.fileName}
        title={legalModal.title}
      />
    </Box>
  );
}

export default SupplierLogin; 